import BusinessList from '@/components/business/BusinessList';
import DownloadApp from '@/components/shared/DownloadApp';
import { ENV } from '@/core/config/env';
import { GET_BUSINESS_LIST } from '@/core/graphql/queries/getBusinessList';
import { BusinessItem } from '@/core/models/businesses/businessList';
import { SearchBusinessRequest } from '@/core/models/businesses/businessRequest';
import { initializeApollo } from '@/lib/apolloClient';
import { slugify } from '@/lib/slugify';
import { getString } from '@/lib/stringUtils';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';

interface Props {
    businesses: BusinessItem[];
    pagination: {
        count: number;
        currentPage: number;
        totalPages: number;
    };
}

const quickCities = [
    { name: 'Johannesburg', province: 'gauteng' },
    { name: 'Pretoria', province: 'gauteng' },
    { name: 'Cape Town', province: 'western-cape' },
    { name: 'Durban', province: 'kwazulu-natal' },
    { name: 'Gqeberha', province: 'eastern-cape' },
    { name: 'Bloemfontein', province: 'free-state' },
];

const cityToProvinceMap: Record<string, string> = {
    Johannesburg: 'gauteng',
    Pretoria: 'gauteng',
    'Cape Town': 'western-cape',
    Durban: 'kwazulu-natal',
    Gqeberha: 'eastern-cape',
    Bloemfontein: 'free-state',
};

const quickProvinces = [
    'Gauteng',
    'Western Cape',
    'KwaZulu-Natal',
    'Eastern Cape',
    'Free State',
    'Mpumalanga',
    'Limpopo',
    'North West',
    'Northern Cape',
];

export default function GoldwagenNearMePage({ businesses, pagination }: Props) {
    const router = useRouter();
    const { query } = router;

    const searchParams = {
        category: getString(query.category),
        province: getString(query.province),
        city: getString(query.city),
        suburb: getString(query.suburb),
        text: '',
        page: query.page ? parseInt(query.page as string, 10) : 1,
    };

    const toTitleCase = (value: string) =>
        value
            .split('-')
            .filter(Boolean)
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
            .join(' ');

    const provinceName = searchParams.province ? toTitleCase(searchParams.province) : '';
    const cityName = searchParams.city ? toTitleCase(searchParams.city) : '';
    const suburbName = searchParams.suburb ? toTitleCase(searchParams.suburb) : '';
    const hasLocationFilter = Boolean(provinceName || cityName || suburbName);
    const locationLabel = suburbName
        ? [suburbName, cityName, provinceName].filter(Boolean).join(', ')
        : cityName
          ? [cityName, provinceName].filter(Boolean).join(', ')
          : provinceName;

    const locationSuffix = hasLocationFilter
        ? `in ${locationLabel}`
        : 'in South Africa';

    const titleLocationLabel = hasLocationFilter
        ? `in ${locationLabel}`
        : 'Near Me';

    const pageTitle = hasLocationFilter
        ? `Goldwagen ${locationLabel} | Car Parts, Accessories & Auto Service`
        : 'Goldwagen Near Me | Car Parts, Accessories & Auto Service';

    const pageDescription = hasLocationFilter
        ? `Find your nearest Goldwagen store in ${locationLabel}. Quality car parts, accessories, tyres, batteries, oils, and auto services. Trusted automotive retailer with competitive prices.`
        : 'Find your nearest Goldwagen store. Quality car parts, accessories, tyres, batteries, oils, and auto services across South Africa. Trusted automotive retailer with competitive prices.';

    const baseUrl = `${ENV.DOMAIN_URL}/goldwagen-near-me`;
    const canonicalParams = new URLSearchParams();
    if (searchParams.province) canonicalParams.set('province', searchParams.province);
    if (searchParams.city) canonicalParams.set('city', searchParams.city);
    if (searchParams.suburb) canonicalParams.set('suburb', searchParams.suburb);
    if (searchParams.page > 1) canonicalParams.set('page', String(searchParams.page));
    const canonicalUrl = canonicalParams.toString()
        ? `${baseUrl}?${canonicalParams.toString()}`
        : baseUrl;

    const suburbLinksByCity: Record<string, string[]> = {
        Johannesburg: ['Sandton', 'Randburg', 'Roodepoort', 'Soweto', 'Midrand'],
        Pretoria: ['Centurion', 'Hatfield', 'Menlyn', 'Brooklyn', 'Silverton'],
        'Cape Town': ['Bellville', 'Century City', 'Milnerton', 'Woodstock', 'Somerset West'],
        Durban: ['Umhlanga', 'Pinetown', 'Westville', 'Berea', 'Amanzimtoti'],
        Gqeberha: ['Walmer', 'Newton Park', 'Summerstrand', 'Korsten', 'Uitenhage'],
        Bloemfontein: ['Universitas', 'Langenhoven Park', 'Brandwag', 'Westdene', 'Hilton'],
    };

    const suburbLinks = cityName && suburbLinksByCity[cityName] ? suburbLinksByCity[cityName] : [];
    const locationKeywords = hasLocationFilter ? locationLabel : 'South Africa';
    const keywordList = [
        `goldwagen ${locationKeywords}`,
        `goldwagen near me ${locationKeywords}`,
        `goldwagen car parts ${locationKeywords}`,
        `goldwagen accessories ${locationKeywords}`,
        `goldwagen tyres ${locationKeywords}`,
        `goldwagen store ${locationKeywords}`,
        `goldwagen auto service ${locationKeywords}`,
    ].join(', ');

    const faqItems = [
        {
            question: `How do I find a Goldwagen store ${locationSuffix}?`,
            answer: hasLocationFilter
                ? `You are filtering for ${locationLabel}. Use the province, city, or suburb filters to find the closest Goldwagen stores, or clear filters to see all Goldwagen locations.`
                : 'Use the province, city, or suburb filters to find your nearest Goldwagen store. You can also browse Goldwagen near me to see all locations across South Africa.',
        },
        {
            question: `What products does Goldwagen sell ${locationSuffix}?`,
            answer: 'Goldwagen offers a wide range of automotive products including car parts, tyres, batteries, oils and lubricants, filters, brake pads, shock absorbers, car accessories, tools, and car care products. Most stores also offer fitment services.',
        },
        {
            question: 'Does Goldwagen offer installation services?',
            answer: 'Yes, most Goldwagen stores offer professional fitment services for tyres, batteries, wiper blades, and other parts. Some locations also provide oil changes and basic maintenance services. Contact your local store for available services.',
        },
        {
            question: 'Can I order online from Goldwagen?',
            answer: 'Many Goldwagen stores offer online ordering with in-store pickup or delivery options. You can browse products online, check availability, and reserve items at your nearest store. Contact details are available for each location.',
        },
        {
            question: 'Does Goldwagen offer warranties on their products?',
            answer: 'Yes, Goldwagen provides warranties on most automotive parts and accessories. Battery and tyre warranties vary by brand and product. Always ask about warranty terms when purchasing, and keep your receipts for warranty claims.',
        },
    ];

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content={keywordList} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@graph': [
                                {
                                    '@type': 'LocalBusiness',
                                    '@id': canonicalUrl,
                                    name: `Goldwagen ${titleLocationLabel}`,
                                    description: pageDescription,
                                    url: canonicalUrl,
                                    areaServed: locationKeywords,
                                    knowsAbout: [
                                        'Car Parts',
                                        'Automotive Accessories',
                                        'Tyres',
                                        'Batteries',
                                        'Oils and Lubricants',
                                        'Car Care Products',
                                    ],
                                },
                                {
                                    '@type': 'FAQPage',
                                    mainEntity: faqItems.map((item) => ({
                                        '@type': 'Question',
                                        name: item.question,
                                        acceptedAnswer: {
                                            '@type': 'Answer',
                                            text: item.answer,
                                        },
                                    })),
                                },
                            ],
                        }),
                    }}
                />
            </Head>
            <section className="relative overflow-hidden bg-[#0f1512] text-white">
                <div
                    className="absolute inset-0 opacity-35"
                    style={{
                        backgroundImage: "radial-gradient(circle at 15% 20%, rgba(218, 165, 32, 0.35), transparent 45%), radial-gradient(circle at 80% 10%, rgba(184, 134, 11, 0.3), transparent 40%), radial-gradient(circle at 65% 85%, rgba(238, 180, 34, 0.25), transparent 45%)",
                    }}
                />
                <div className="container relative py-12 md:py-16">
                    <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
                        <div className="lg:col-span-7">
                            <p className="text-xs uppercase tracking-[0.3em] text-yellow-200">Goldwagen Automotive</p>
                            <h1 className="mt-3 text-3xl md:text-5xl font-bold leading-tight">
                                Goldwagen {titleLocationLabel}
                            </h1>
                            <p className="mt-4 text-base md:text-lg text-yellow-100">
                                Find your nearest Goldwagen store. Quality car parts, accessories, tyres, batteries, 
                                oils, and fitment services at competitive prices.
                            </p>
                            <div className="mt-6 flex flex-wrap items-center gap-3">
                                <Link
                                    href="#business-filters"
                                    className="bg-white text-slate-900 px-5 py-2.5 rounded-md text-sm font-semibold shadow"
                                >
                                    Find your Goldwagen
                                </Link>
                                <Link
                                    href="/goldwagen-near-me"
                                    className="bg-yellow-600 text-white px-5 py-2.5 rounded-md text-sm font-semibold shadow"
                                >
                                    All Goldwagen stores
                                </Link>
                                <span className="text-xs text-yellow-100">Quick city shortcuts below.</span>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-semibold text-yellow-100">Popular Goldwagen locations</p>
                                    <span className="rounded-full bg-yellow-600/20 px-2.5 py-1 text-xs text-yellow-100">
                                        Quality
                                    </span>
                                </div>
                                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                    <Link
                                        href="/goldwagen-near-me/goldwagen/gauteng/johannesburg"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-yellow-300"
                                    >
                                        Goldwagen Johannesburg
                                    </Link>
                                    <Link
                                        href="/goldwagen-near-me/goldwagen/western-cape/cape-town"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-yellow-300"
                                    >
                                        Goldwagen Cape Town
                                    </Link>
                                    <Link
                                        href="/goldwagen-near-me/goldwagen/kwazulu-natal/durban"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-yellow-300"
                                    >
                                        Goldwagen Durban
                                    </Link>
                                    <Link
                                        href="/goldwagen-near-me/goldwagen/gauteng/pretoria"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-yellow-300"
                                    >
                                        Goldwagen Pretoria
                                    </Link>
                                </div>
                                <div className="mt-5 grid gap-3 sm:grid-cols-3 text-xs text-yellow-100">
                                    <div className="rounded-lg border border-white/10 px-3 py-2">Car parts & tyres</div>
                                    <div className="rounded-lg border border-white/10 px-3 py-2">Accessories</div>
                                    <div className="rounded-lg border border-white/10 px-3 py-2">Fitment service</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container my-10">
                <div className="grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-8">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-2xl font-semibold text-slate-900">Your Trusted Goldwagen Automotive Store</h2>
                            <p className="mt-3 text-sm text-slate-600">
                                Looking for Goldwagen {locationSuffix}? CarSparesEzyFind helps you locate
                                Goldwagen stores and automotive centres across South Africa. Goldwagen offers a comprehensive 
                                range of car parts, accessories, tyres, batteries, and automotive services at competitive prices. 
                                Use the filters to find your nearest Goldwagen store by province, city, or suburb.
                            </p>
                            <p className="mt-3 text-sm text-slate-600">
                                Whether you need a Goldwagen near me in Sandton, a Goldwagen store in
                                Roodepoort, or Goldwagen car parts in Randburg, this page helps you find the most
                                convenient location. Goldwagen stores stock quality OEM and aftermarket parts, premium tyres, 
                                long-lasting batteries, and a full range of car care products. Many locations also offer 
                                professional fitment services for tyres, batteries, and other components.
                            </p>
                            <p className="mt-3 text-sm text-slate-600">
                                Popular searches include Goldwagen Johannesburg Gauteng, Goldwagen car parts Johannesburg,
                                Goldwagen tyres Cape Town, and Goldwagen accessories Durban.
                                Products include engine parts, brake components, filters, oils and lubricants, car batteries, 
                                tyres (passenger and commercial), shock absorbers, tools, car cleaning products, and automotive 
                                accessories. Visit your nearest Goldwagen for expert advice and competitive pricing.
                            </p>
                            <p className="mt-3 text-sm text-slate-600">
                                Goldwagen stores are independently operated but maintain consistent quality standards and 
                                competitive pricing. Most locations offer knowledgeable staff who can help you find the right 
                                parts for your vehicle. Check with your local Goldwagen store for specific product availability, 
                                services, operating hours, and current promotions. Many stores also offer loyalty programs 
                                and bulk purchase discounts.
                            </p>
                        </div>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h3 className="text-lg font-semibold text-slate-900">Goldwagen Products & Services</h3>
                            <ul className="mt-4 space-y-2 text-sm text-slate-600">
                                <li>Car parts & components</li>
                                <li>Tyres (passenger & commercial)</li>
                                <li>Batteries & electrical</li>
                                <li>Oils & lubricants</li>
                                <li>Filters & maintenance parts</li>
                                <li>Accessories & car care</li>
                            </ul>
                            <h3 className="mt-6 text-lg font-semibold text-slate-900">Why Choose Goldwagen</h3>
                            <p className="mt-3 text-sm text-slate-600">
                                Goldwagen offers competitive pricing on quality automotive products, knowledgeable staff, 
                                wide product range, convenient locations, and professional fitment services at most stores.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container my-10">
                <div className="grid gap-6 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-900">Find Goldwagen by city</h2>
                                    <p className="text-sm text-slate-600 mt-1">
                                        Fast links to Goldwagen stores in major cities and metro areas.
                                    </p>
                                </div>
                                <Link
                                    href="/goldwagen-near-me"
                                    className="text-sm font-semibold text-yellow-700"
                                >
                                    View all locations
                                </Link>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {quickCities.map((city) => (
                                    <Link
                                        key={city.name}
                                        href={`/goldwagen-near-me/goldwagen/${city.province}/${slugify(city.name)}`}
                                        className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-yellow-500 hover:text-yellow-700"
                                    >
                                        {city.name}
                                    </Link>
                                ))}
                            </div>
                            <div className="mt-6 grid gap-3 md:grid-cols-3">
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step 1</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">Choose location</p>
                                    <p className="mt-1 text-xs text-slate-600">Select your province, city, or suburb.</p>
                                </div>
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step 2</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">Find nearest store</p>
                                    <p className="mt-1 text-xs text-slate-600">View closest Goldwagen stores.</p>
                                </div>
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step 3</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">Visit or call</p>
                                    <p className="mt-1 text-xs text-slate-600">Contact or visit your local Goldwagen.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-lg font-semibold text-slate-900">Browse by province</h2>
                            <p className="text-sm text-slate-600 mt-1">
                                Filter by province, then drill down to city and suburb.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {quickProvinces.map((province) => (
                                    <Link
                                        key={province}
                                        href={`/goldwagen-near-me/goldwagen/${slugify(province)}`}
                                        className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-yellow-500 hover:text-yellow-700"
                                    >
                                        {province}
                                    </Link>
                                ))}
                            </div>
                        </div>
                        <div className="mt-6 rounded-2xl border border-yellow-100 bg-yellow-50 p-5">
                            <h3 className="text-sm font-semibold text-slate-900">Shopping tips</h3>
                            <ul className="mt-2 space-y-2 text-sm text-slate-700">
                                <li>Bring your vehicle details (make, model, year) for accurate parts.</li>
                                <li>Ask about warranties on batteries and tyres.</li>
                                <li>Check for current promotions and bulk discounts.</li>
                                <li>Many stores offer fitment services - book ahead.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container my-10" aria-labelledby="suburb-links-heading">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 id="suburb-links-heading" className="text-xl font-semibold text-slate-900">
                        {cityName
                            ? `Goldwagen Stores by Suburb in ${cityName}`
                            : 'Goldwagen Stores by City in South Africa'}
                    </h2>
                    <p className="mt-2 text-sm text-slate-600">
                        {cityName
                            ? `Explore Goldwagen stores in ${cityName} suburbs to find your nearest location.`
                            : 'Jump to popular cities and metros to find Goldwagen stores near you.'}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {cityName && suburbLinks.length > 0
                            ? suburbLinks.map((suburb) => (
                                  <Link
                                      key={suburb}
                                      href={`/goldwagen-near-me?city=${slugify(cityName)}&suburb=${slugify(suburb)}`}
                                      className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-yellow-500 hover:text-yellow-700"
                                  >
                                      {`Goldwagen ${suburb}`}
                                  </Link>
                              ))
                            : quickCities.map((city) => (
                                  <Link
                                      key={city.name}
                                      href={`/goldwagen-near-me/goldwagen/${city.province}/${slugify(city.name)}`}
                                      className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-yellow-500 hover:text-yellow-700"
                                  >
                                      {`Goldwagen ${city.name}`}
                                  </Link>
                              ))}
                    </div>
                </div>
            </section>

            <div className="my-10">
                <div className="container" id="business-filters">
                    <BusinessList
                        searchParams={searchParams}
                        defaultCategoryIds="5019"
                        initialBusinesses={businesses}
                        initialPagination={pagination}
                    />
                </div>
            </div>

            <section className="container my-12">
                <div className="grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <p className="text-xs uppercase tracking-[0.25em] text-yellow-700">Goldwagen Directory</p>
                            <h2 className="mt-3 text-2xl font-semibold text-slate-900">
                                Goldwagen stores {locationSuffix}
                            </h2>
                            <p className="mt-3 text-sm text-slate-600">
                                Use this page to find Goldwagen near you, or drill into a specific province,
                                city, or suburb. Results update instantly as you apply filters.
                            </p>
                            <p className="mt-4 text-sm text-slate-600">
                                Browse Goldwagen locations, stores, and service centres in
                                one place. Compare locations, products, and contact details side-by-side.
                            </p>
                            <div className="mt-5 rounded-xl bg-slate-50 p-4 text-xs text-slate-600">
                                {hasLocationFilter
                                    ? `Showing Goldwagen stores for ${locationLabel}. Change province, city, or suburb to explore nearby areas.`
                                    : 'Tip: Start with a province, then narrow by city or suburb for your nearest Goldwagen store.'}
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-lg font-semibold text-slate-900">
                                Frequently Asked Questions (Goldwagen SEO)
                            </h2>
                            <div className="mt-4 space-y-4">
                                {faqItems.map((item) => (
                                    <div key={item.question} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                        <h3 className="text-sm font-semibold text-slate-900">{item.question}</h3>
                                        <p className="mt-2 text-sm text-slate-600">{item.answer}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <DownloadApp />
        </>
    );
}

export const getStaticProps = async () => {
    const apolloClient = initializeApollo();
    const businessRequest: SearchBusinessRequest = {
        companyId: null,
        companyName: '',
        franchiseeId: null,
        statusIds: '1',
        categoryIds: '5019',
        provinceIds: '',
        cityIds: '',
        suburbIds: '',
        page: 1,
        size: 12,
    };

    const { data: businessData } = await apolloClient.query({
        query: GET_BUSINESS_LIST,
        variables: businessRequest,
    });

    const isStaticExport = process.env.BUILD_MODE === 'export';
    
    return {
        props: {
            businesses: businessData?.getBusinessList?.result || [],
            pagination: {
                count: businessData?.getBusinessList?.count ?? 0,
                currentPage: businessData?.getBusinessList?.currentPage ?? 1,
                totalPages: businessData?.getBusinessList?.totalPages ?? 0,
            },
        },
        ...(!isStaticExport && { revalidate: 3600 }),
    };
};
